// The poll half of `lib/paged.ts` (plan 101 row 17-21) — split out the same way
// `paged-slices.ts` and `paged-view.ts` were, and with its own reason to change:
// this owns HOW a loaded window is re-read and WHEN the timer fires, while
// `paged.ts` owns whether the answer may still be merged.
//
// Why a re-WALK and not a re-read of each held cursor: a keyset token names the
// last row of the page before it. Re-reading page two at its old token after a
// row was inserted at the head drops the row pushed off page one (a gap); after a
// row on page one is deleted, page one's refill is also page two's first row (a
// duplicate). Walking from page one and following each FRESH `next_cursor` reads
// the window the server would serve a first-time visitor — still keyset, never
// an offset.

import { createEffect, onCleanup } from 'solid-js';
import type { PageSlices } from './paged-slices';
import type { Page, PagedPoll } from './paged-types';

/** One page of a re-walked window, with the cursor that addressed it. */
export type WalkedPage<T> = { readonly at: string | null; readonly page: Page<T> };

/** Read `depth` pages from page one, following each fresh `next_cursor`; shorter at the end. */
async function walkWindow<T>(
  read: (cursor: string | null) => Promise<Page<T>>,
  depth: number,
): Promise<WalkedPage<T>[]> {
  const walked: WalkedPage<T>[] = [];
  let at: string | null = null;
  while (walked.length < depth) {
    const page: Page<T> = await read(at);
    walked.push({ at, page });
    if (page.next_cursor === null) break;
    at = page.next_cursor;
  }
  return walked;
}

/**
 * Fire `tick` every `every()` ms in the current owner, re-armed when the cadence
 * moves and stopped at `false` or disposal. A hidden tab skips its ticks — the
 * foreground-only behaviour TanStack's `refetchInterval` had, which this replaces.
 */
function createPollTimer(every: () => number | false, tick: () => void): void {
  createEffect(() => {
    const ms = every();
    if (ms === false) return;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      tick();
    }, ms);
    onCleanup(() => clearInterval(id));
  });
}

/**
 * Re-walk the loaded window on `poll.every()`, one walk at a time, in the current
 * owner — or, with no `poll` asked for, nothing at all. `slices` says how deep (none held yet skips the tick) and, by its epoch,
 * whether anything merged or dropped while the walk was out. `busy` is a read
 * already in flight (a load-more, a reload's forced page one): the tick skips.
 */
export function createWindowPoll<T>(
  poll: PagedPoll<T> | undefined,
  slices: Pick<PageSlices<T>, 'count' | 'epoch'>,
  busy: () => boolean,
  land: (walked: readonly WalkedPage<T>[]) => void,
): void {
  if (!poll) return;
  let walking = false;
  createPollTimer(poll.every, () => {
    const depth = slices.count();
    if (walking || depth === 0 || busy()) return;
    walking = true;
    const at = slices.epoch();
    walkWindow(poll.read, depth)
      .then((walked) => {
        if (at === slices.epoch()) land(walked);
      })
      // A failed tick keeps the last good window on screen, as a failed TanStack
      // background refetch did; the next tick re-walks.
      .catch(() => undefined)
      .finally(() => {
        walking = false;
      });
  });
}
